import { format } from 'date-fns';
import { formatToTimeZone } from 'date-fns-timezone';

export const HasProperty = ( a, b ) => Object.prototype.hasOwnProperty.call( a, b );

export const ObjToArray = ( object ) => {

	if ( object === null || object === undefined ) {
		return [];
	}

	const keys  = Object.keys( object );
	const array = keys.reduce( ( arr, key ) => {

		const obj = object[key];

		if ( !HasProperty( obj, 'key' ) ) {
			obj.key = key;
		}

		arr.push( obj );

		return arr;

	}, [] );

	return array;

};

export const uid = ( name ) => {
	const salt = Math.floor( Math.random() * 9000 ) + 1000;
	const now  = Date.now().toString();

	return `${name}-${salt}${now.substr( 5, now.length )}`;
};

export const capitalize = string => string.charAt( 0 ).toUpperCase() + string.slice( 1 );

export const iterate = ( obj, callback ) => {
	if ( Array.isArray( obj ) ) {
		obj.forEach( ( item, i ) => callback( item, i ) );
		return;
	}

	const keys = Object.keys( obj );
	keys.forEach( ( key ) => {
		const item = obj[key];

		callback( item, key );
	} );
};

export const clone = ( obj ) => {
	const newObj = Array.isArray( obj ) ? [] : {};

	iterate( obj, ( value, key ) => {
		if ( value === null ) {
			newObj[key] = null;

			return;
		}

		if ( value instanceof File || value instanceof Date ) {
			newObj[key] = value;
			return;
		}

		newObj[key] = typeof value === 'object' ? clone( value ) : value;
	} );

	return newObj;
};

export const ensureLength = ( num, length ) => {
	let str = num.toString();

	while ( str.length < length ) {
		str = `0${str}`;
	}

	return str;
};

export const combine = ( obj1, obj2, destructive = false ) => {
	const newObj = destructive ? obj1 : clone( obj1 );

	iterate( obj2, ( value, key ) => {
		const valueIsObject = ( typeof value === 'object' && value !== null );

		if ( !HasProperty( newObj, key ) || ( valueIsObject && value.constructor === File ) ) {
			newObj[key] = ( valueIsObject && !destructive ) ? clone( value ) : value;

			return;
		}

		newObj[key] = valueIsObject ? combine( newObj[key], value, destructive ) : value;
	} );

	return newObj;
};

export const Compare = ( a, b ) => {
	if ( a === null && b === null ) {
		return true;
	}

	if ( a === null || b === null ) {
		return false;
	}

	const aKeys = Object.keys( a );
	const bKeys = Object.keys( b );

	if ( aKeys.length !== bKeys.length ) {
		// can't be the same if they have a different
		// key length
		return false;
	}

	let identical = true;
	for ( let i = 0; i < aKeys.length; i += 1 ) {
		const aKey = aKeys[i];

		if ( typeof a[aKey] === 'object' ) {

			if ( !Compare( a[aKey], b[aKey] ) ) {
				identical = false;
			}

		}

		if ( a[aKey] !== b[aKey] ) {
			identical = false;
		}
	}

	return identical;
};

export const SetDefault = ( obj, defaultObj ) => {

	const props = Object.keys( defaultObj );

	props.forEach( ( prop ) => {
		if ( HasProperty( obj, prop ) ) {
			// don't need to set a default if the value
			// already exists
			return;
		}

		const defaultVal = defaultObj[prop];

		if ( typeof defaultVal === 'object' ) {
			// recurse if this is an object type
			// such as Array or Object
			obj[prop] = SetDefault( {}, defaultVal ); // eslint-disable-line

			return;
		}

		// set primitive value
		obj[prop] = defaultVal; // eslint-disable-line
	} );

	return obj;
};

export const Flatten = ( objToFlatten, prop = '' ) => {

	if ( ( objToFlatten instanceof Date ) || objToFlatten === null ) {
		return objToFlatten;
	}

	return Object.keys( objToFlatten ).reduce( ( obj, key ) => {

		const value = objToFlatten[key];

		const upperCaseKey = key.charAt( 0 ).toUpperCase() + key.slice( 1 );

		const newKey =  prop ? `${prop}${upperCaseKey}` : key;

		if ( Array.isArray( value ) ) {
			const added = {};
			added[key]  = value;

			return {
				 ...obj,
				 ...added,
			};
		}

		if ( typeof value === 'object' ) {
			const flattenObj = Flatten( value, newKey );
			return { ...obj, ...flattenObj };
		}

		if ( prop ) {
			obj[newKey] = value; // eslint-disable-line
			return obj;
		}

		obj[key] = value; // eslint-disable-line

		return obj;

	}, {} );

};

export const ObjPropsToString = ( obj ) => { // eslint-disable-line

	return Object.keys( obj ).reduce( ( o, key ) => {

		const value = obj[key];

		if ( typeof value !== 'string' ) {
			o[key] = value.toString(); //eslint-disable-line
			return o;
		}

		o[key] = value; // eslint-disable-line
		return o;

	}, {} );

};

// Assumes the return obj only needs to be 2 levels deeps
export const Unflatten = ( objToUnflatten, unflattenKeys ) => { // eslint-disable-line

	return Object.keys( objToUnflatten ).reduce( ( obj, key ) => {

		const value = objToUnflatten[key];

		const prefix = unflattenKeys.find( a => key.startsWith( a ) );

		if ( !prefix ) {
			obj[key] = value; // eslint-disable-line
			return obj;
		}

		const keyWithPrefixRemoved = ( () => {
			const lengthOfPrefix = prefix.length;

			const remaining = key.substr( lengthOfPrefix );

			return remaining.charAt( 0 ).toLowerCase() + remaining.slice( 1 );
		} )();

		if ( !obj[prefix] ) {
			obj[prefix] = {}; // eslint-disable-line
		}

		obj[prefix][keyWithPrefixRemoved] = value; // eslint-disable-line

		return obj;

	}, {} );

};

// Assumes the obj is 2 levels deep
export const FilterToAPI = ( obj ) => { // eslint-disable-line

	if ( !obj ) {
		return [];
	}

	return Object.keys( obj ).reduce( ( arr, key ) => {

		const value = obj[key];

		arr.push( [key, value] );

		return arr;

	}, [] );
};

export const SortToAPI = ( { key, direction } ) => {
	if ( !key ) {
		return '';
	}

	const operator = direction > 0 ? '+' : '-';

	return `${operator}${key}`;
};

export const trim = ( objectSchema, trimmableObject ) => {
	const keys = Object.keys( objectSchema );

	return keys.reduce( ( obj, key ) => {
		const added = {};
		const value = trimmableObject[key];

		if ( typeof value !== 'object' && value !== undefined ) {
			added[key] = value;
		}

		else if ( value === null || value === undefined ) {
			added[key] = objectSchema[key]; // default to starting value
		}

		else if ( typeof value === 'object' ) {

			if ( Array.isArray( value ) ) {

				// hopefully, the objectSchema will have at least
				// one value which we can check against.
				const [arraySchema] = objectSchema[key];

				if ( !arraySchema ) {
					console.warn( `Due to lack of schema for array items of value "${key}", the entire array and all the properties of it's children remain fully intact. In order to better leverage this function, please provide at least one item in the array of your objectSchema at key "${key}".` );

					added[key] = [...value];
				}

				else {
					added[key] = value.map( ( a ) => {
						if ( typeof a !== 'object' || a === null ) {
							return a;
						}

						return trim( arraySchema, a );
					} );
				}

			}

			else {
				added[key] = trim( objectSchema[key], value );
			}

		}

		return Object.assign( obj, added );
	}, {} );
};

// Check here for format string syntax
// https://date-fns.org/v1.30.1/docs/format
export const FormatDate = ( d, formatString, timeZone = false ) => {

	const date = d instanceof Date ? d : new Date( d );
	// if the date is a perfect date,
	// i.e. at exactly midnight, then we
	// want to correct the date
	if ( timeZone ) {
		return formatToTimeZone( date, formatString, { timeZone } );
	}

	return format( date, formatString );

};

export const RemoveEndSlashes = ( str ) => {
	const regexForEndSlashes = /(?:[^:]{1})([\\/]{1})$/;

	return str.replace( regexForEndSlashes, '$1' );
};
